import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '../../api/client'
import { type SocialProvider, completeSocialLogin } from '../../api/userland'
import { useAccountVault } from '../../hooks/useAccountVault'
import {
  clearPendingReveal,
  isOAuthPopup,
  markPendingRevealConfirmed,
  peekOAuthResumeIntent,
  peekPendingReveal,
  publishOAuthSignal,
  takeOAuthResume,
} from '../../lib/oauthBridge'
import { writeSocialPending } from '../../lib/socialPending'
import { isTelegramWebView, socialResumeUrl } from '../../lib/telegramWebView'
import soft from '../../styles/soft.module.css'
import { useAccountSession } from '../../wallet/AccountSessionContext'
import { useAuthShell } from '../../wallet/AuthShellContext'
import { useWallets } from '../../wallet/WalletsContext'
import { SoftSpinner } from '../ui/pixel-toast'
import { ExternalBrowserCard } from './AuthContinue'
import { AuthHero } from './AuthHero'
import styles from './AuthShell.module.css'

function providerLabel(provider: SocialProvider): string {
  return provider === 'telegram' ? 'Telegram' : 'Google'
}

export function AuthCallbackPage({ provider }: { provider: SocialProvider }) {
  const navigate = useNavigate()
  const { openAuth } = useAuthShell()
  const session = useAccountSession()
  const vault = useAccountVault()
  const { reload } = useWallets()
  const [error, setError] = useState<string | null>(null)
  const [resumeUrl, setResumeUrl] = useState<string | null>(null)
  const ran = useRef(false)

  useEffect(() => {
    if (ran.current) return
    ran.current = true
    const params = new URLSearchParams(window.location.search)
    const denied = params.get('error')
    const code = params.get('code')
    const state = params.get('state') ?? ''
    if (denied || !code) {
      const message = denied === 'access_denied' ? 'Login was cancelled.' : 'Login failed.'
      publishOAuthSignal({ ok: false, provider, error: message })
      if (isOAuthPopup()) {
        window.close()
        return
      }
      setError(message)
      return
    }
    const popup = isOAuthPopup()
    const finishHere = (path: string, next?: () => void) => {
      if (popup) {
        window.close()
        return
      }
      next?.()
      navigate(path, { replace: true })
    }
    void completeSocialLogin({ provider, code, state })
      .then(async () => {
        await session.reload()
        await vault.unlockFromSession()
        await reload()
        // a pending reveal is confirmed only by a Confirm; a plain login means
        // that reveal was left — it must not open on it (r9x review)
        if (peekPendingReveal()) {
          if (peekOAuthResumeIntent() === 'confirm') markPendingRevealConfirmed()
          else clearPendingReveal()
        }
        publishOAuthSignal({ ok: true, provider })
        if (isTelegramWebView()) {
          setResumeUrl(socialResumeUrl(window.location.origin, provider))
          return
        }
        const resume = takeOAuthResume()
        finishHere(resume?.path || '/discover')
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.code === 'register_required' && err.ticket) {
          writeSocialPending({ provider, ticket: err.ticket })
          publishOAuthSignal({ ok: false, provider, register: true, ticket: err.ticket })
          if (isTelegramWebView()) {
            setResumeUrl(socialResumeUrl(window.location.origin, provider, err.ticket))
            return
          }
          const resume = takeOAuthResume()
          finishHere(resume?.path || '/discover', () => openAuth('ton-recovery'))
          return
        }
        const message = err instanceof ApiError ? err.message : 'Login failed.'
        publishOAuthSignal({ ok: false, provider, error: message })
        if (popup) {
          window.close()
          return
        }
        setError(message)
      })
  }, [navigate, openAuth, provider, reload, session, vault])

  if (resumeUrl) {
    return (
      <div className={styles.callback}>
        <div className={styles.dialog}>
          <div className={styles.sheet}>
            <ExternalBrowserCard
              resumeUrl={resumeUrl}
              hasTicket={resumeUrl.includes('ticket=')}
              onBack={() => navigate('/discover', { replace: true })}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.callback}>
      <div className={styles.dialog}>
        <div className={styles.sheet}>
          <div className={styles.chooser}>
            <AuthHero kind="signin" />
            <h2 className={styles.welcome}>
              {error ? 'Couldn’t sign in' : `Signing in with ${providerLabel(provider)}`}
            </h2>
            {error ? (
              <p className={styles.error}>{error}</p>
            ) : (
              <>
                <span className={styles.callbackSpin}>
                  <SoftSpinner size={20} />
                </span>
                <p className={styles.lead}>Just a moment.</p>
              </>
            )}
            {error ? (
              <button
                type="button"
                className={`${soft.btn} ${styles.method} ${styles.methodCentre}`}
                onClick={() => navigate('/discover', { replace: true })}
              >
                Back to Wils.trade
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
